// Each screen in a file of its own — 24 Sep 2026, the speed review.
//
// WHY. Every screen sat in the one file the brain loads first, about half of the brain's own
// code, so opening any page downloaded and parsed all of them before Clerk could begin signing
// in. That file now holds the shell and the framework, and each screen is a file of its own.
//
// EVERY SCREEN, AT ONCE, IN THE BACKGROUND. Each screen's `preload` is asked at boot
// (`preloadScreens`, screens.ts), while Clerk signs in — a round trip to its own host at least —
// so a screen is here by the time it is drawn, and a click to another page never waits on the
// network. It also means a tab opened before a release already holds every screen when the
// release deletes the old files (deploy.yml syncs the bundle with `--delete`).
//
// NO FALLBACK FOR A SCREEN THAT HAS ARRIVED. React holds a boundary's content back for up to
// 300 ms once its fallback has shown, so a screen that suspended on its first draw would appear
// later than it did before the split. A screen whose file is here is drawn directly; only one
// still on its way suspends. A load that fails says so with a Reload, and never reloads on its
// own (NewVersion.tsx: a half-typed message goes with a reload).
import {
  Component,
  createElement,
  lazy,
  Suspense,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Button } from '../ui/atoms.tsx';

/** A screen's file did not arrive: a release has since replaced it, or there is no network. */
export class ScreenLoadFailed extends Error {
  constructor(cause: unknown) {
    super(`the screen did not load: ${cause instanceof Error ? cause.message : String(cause)}`, {
      cause,
    });
    this.name = 'ScreenLoadFailed';
  }
}

export type LazyScreen<P extends object> = ComponentType<P> & {
  /** Fetches the screen's file. Never rejects: a fetch that failed is made again when next asked. */
  preload(): Promise<void>;
};

export function lazyScreen<P extends object>(load: () => Promise<ComponentType<P>>): LazyScreen<P> {
  let arrived: ComponentType<P> | null = null;
  let loading: Promise<ComponentType<P>> | null = null;
  const fresh = () => lazy(async () => ({ default: await start() }));
  // React keeps a lazy component's failure for good, so a failed load gets a fresh one: the next
  // time the screen is opened, its file is asked for again.
  let Lazy = fresh();
  function start(): Promise<ComponentType<P>> {
    loading ??= load().then(
      (component) => (arrived = component),
      (error: unknown) => {
        loading = null;
        Lazy = fresh();
        throw new ScreenLoadFailed(error);
      },
    );
    return loading;
  }

  function Screen(props: P) {
    // Decided once for each time the screen is mounted. One drawn through the lazy path stays on
    // it, or a later render would change the element's type and remount it, losing what was typed.
    const [direct] = useState(() => ({ screen: arrived }));
    if (direct.screen !== null) return createElement(direct.screen, props);
    return (
      <ScreenBoundary>
        <Suspense fallback={null}>{createElement(Lazy, props)}</Suspense>
      </ScreenBoundary>
    );
  }
  return Object.assign(Screen, {
    preload: () =>
      start().then(
        () => undefined,
        () => undefined,
      ),
  });
}

/**
 * A failed load's message, in the new-version strip's place and words. Only a load that failed
 * is this boundary's: any other error goes on up, as it did before the split.
 */
export class ScreenBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown): { error: unknown } {
    return { error };
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (!(error instanceof ScreenLoadFailed)) throw error;
    return (
      <div className="new-version" role="alert">
        <span>
          This page did not load. The brain may have been updated since this tab opened. Reload when
          you are between things. Anything typed and not yet saved goes with it.
        </span>
        <Button kind="accent" size="sm" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    );
  }
}
